import React, { useState, useRef, useEffect, useMemo } from 'react';
import { CiCalendar } from "react-icons/ci";
import { BsChevronRight, BsChevronLeft, BsChevronDown } from "react-icons/bs";
import { IoMdCheckmark } from "react-icons/io";
import { Select, SelectItem, Tooltip } from '@heroui/react';

const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

const startOfDay = (d) => {
    const newDate = new Date(d);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
};

const TODAY = startOfDay(new Date());
const MIN_DATE = new Date(2020, 0, 1);

const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
};

const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
};

const getWeekNumber = (d) => {
    const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const dayNum = date.getUTCDay() || 7;
    date.setUTCDate(date.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
};

const getWeekRange = (date) => {
    const start = new Date(date);
    const day = start.getDay();
    start.setDate(start.getDate() - day);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { start, end };
};

const isSameDay = (d1, d2) => {
    if (!d1 || !d2) return false;
    return d1.getDate() === d2.getDate() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getFullYear() === d2.getFullYear();
};

const isDateInRange = (date, start, end) => {
    const d = startOfDay(date);
    const s = startOfDay(start);
    const e = startOfDay(end);
    return d >= s && d <= e;
};

const animationStyles = `
  @keyframes slideIn {
    from { opacity: 0; transform: translateY(-10px) scale(0.98); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
  .animate-slide-in {
    animation: slideIn 0.2s ease-out forwards;
  }
`;

function DayPicker({ viewDate, selectedValue, onSelect, isWeekMode }) {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();

    const daysInMonth = getDaysInMonth(year, month);
    const startDay = getFirstDayOfMonth(year, month);

    const prevMonthDays = getDaysInMonth(year, month - 1);
    const fillerStart = prevMonthDays - startDay + 1;

    const days = [];

    for (let i = 0; i < startDay; i++) {
        days.push({ day: fillerStart + i, type: 'prev', date: new Date(year, month - 1, fillerStart + i) });
    }
    for (let i = 1; i <= daysInMonth; i++) {
        days.push({ day: i, type: 'current', date: new Date(year, month, i) });
    }
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
        days.push({ day: i, type: 'next', date: new Date(year, month + 1, i) });
    }

    const [hoverDate, setHoverDate] = useState(null);

    const isSelected = (date) => {
        if (isWeekMode) {
            const { start, end } = getWeekRange(selectedValue);
            return isDateInRange(date, start, end);
        }
        return isSameDay(date, selectedValue);
    };

    const isHovered = (date) => {
        if (!isWeekMode || !hoverDate) return false;
        const { start, end } = getWeekRange(hoverDate);
        return isDateInRange(date, start, end);
    };

    const isDisabled = (date) => {
        return date > TODAY || date < MIN_DATE;
    };

    const getDayClass = (dateObj) => {
        const date = dateObj.date;
        const disabled = isDisabled(date);
        const isSel = isSelected(date);
        const isHov = isHovered(date);
        const isToday = isSameDay(date, TODAY);
        const isOtherMonth = dateObj.type !== 'current';

        let base = "h-9 w-9 flex items-center justify-center text-sm rounded-full transition-all duration-200 ease-in-out relative z-10 ";

        if (disabled) {
            return base + "text-gray-300 cursor-not-allowed bg-gray-50";
        }

        base += "cursor-pointer ";

        if (isOtherMonth) base += "text-gray-400 ";
        else base += "text-gray-700 ";

        if (isSel) {
            if (isWeekMode) {
                return base + "bg-blue-500 text-white hover:bg-blue-600 font-medium shadow-md";
            }
            return base + "bg-blue-500 text-white font-medium shadow-md";
        }

        if (isHov) {
            return base + "bg-blue-100 text-blue-700 font-medium";
        }

        if (isToday && !isSel) {
            return base + "border border-blue-500 font-medium text-blue-500";
        }

        return base + "hover:bg-gray-100";
    };

    const handleDayClick = (date) => {
        if (!isDisabled(date)) {
            onSelect(date);
        }
    };

    return (
        <div>
            <div className="grid grid-cols-7 mb-2">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
                    <div key={d} className="text-center text-xs text-gray-400 font-medium py-1">{d}</div>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-y-1" onMouseLeave={() => setHoverDate(null)}>
                {days.map((d, i) => (
                    <div
                        key={i}
                        className="flex justify-center"
                        onMouseEnter={() => setHoverDate(d.date)}
                    >
                        <button
                            onClick={() => handleDayClick(d.date)}
                            className={getDayClass(d)}
                            disabled={isDisabled(d.date)}
                        >
                            {d.day}
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

function RangeDayPicker({ viewDate, tempStart, tempEnd, hoverDate, setHoverDate, onSelectDay }) {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();

    const daysInMonth = getDaysInMonth(year, month);
    const startDay = getFirstDayOfMonth(year, month);

    const prevMonthDays = getDaysInMonth(year, month - 1);
    const fillerStart = prevMonthDays - startDay + 1;

    const days = [];

    for (let i = 0; i < startDay; i++) {
        days.push({ day: fillerStart + i, type: 'prev', date: new Date(year, month - 1, fillerStart + i) });
    }
    for (let i = 1; i <= daysInMonth; i++) {
        days.push({ day: i, type: 'current', date: new Date(year, month, i) });
    }
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
        days.push({ day: i, type: 'next', date: new Date(year, month + 1, i) });
    }

    const isSameDate = (d1, d2) => {
        if (!d1 || !d2) return false;
        return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
    };

    const isSelected = (date) => {
        return isSameDate(date, tempStart) || isSameDate(date, tempEnd);
    };

    const isInRange = (dateObj) => {
        if (dateObj.type !== 'current') return false;
        const date = dateObj.date;
        if (tempStart && tempEnd) {
            const start = tempStart < tempEnd ? tempStart : tempEnd;
            const end = tempStart < tempEnd ? tempEnd : tempStart;
            return date > start && date < end;
        }
        if (tempStart && hoverDate) {
            const start = tempStart < hoverDate ? tempStart : hoverDate;
            const end = tempStart < hoverDate ? hoverDate : tempStart;
            return date > start && date < end;
        }
        return false;
    };

    const isStartEdge = (dateObj) => {
        if (dateObj.type !== 'current') return false;
        const date = dateObj.date;
        if (tempStart && tempEnd) return isSameDate(date, tempStart < tempEnd ? tempStart : tempEnd);
        if (tempStart && hoverDate) {
            return isSameDate(date, tempStart < hoverDate ? tempStart : hoverDate);
        }
        return isSameDate(date, tempStart);
    };

    const isEndEdge = (dateObj) => {
        if (dateObj.type !== 'current') return false;
        const date = dateObj.date;
        if (tempStart && tempEnd) return isSameDate(date, tempStart < tempEnd ? tempEnd : tempStart);
        if (tempStart && hoverDate) {
            return isSameDate(date, tempStart < hoverDate ? hoverDate : tempStart);
        }
        return false;
    };

    const isDisabled = (date) => {
        return date > TODAY || date < MIN_DATE;
    };

    const getDayClass = (dateObj) => {
        const date = dateObj.date;
        const disabled = isDisabled(date);
        const isSel = isSelected(date);
        const isToday = isSameDate(date, TODAY);
        const isOtherMonth = dateObj.type !== 'current';

        let base = "h-9 w-9 flex items-center justify-center text-sm rounded-full transition-all duration-150 ease-in-out relative z-10 ";

        if (disabled) {
            return base + "text-gray-300 cursor-not-allowed bg-gray-50";
        }

        base += "cursor-pointer ";

        if (isOtherMonth) base += "text-gray-400 ";
        else base += "text-gray-700 ";

        if (isSel) {
            return base + "bg-blue-500 text-white font-medium shadow-md";
        }

        if (isToday) {
            return base + "border border-blue-500 font-medium text-blue-500";
        }

        return base + "hover:bg-gray-100";
    };

    return (
        <div>
            <div className="grid grid-cols-7 mb-2">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
                    <div key={d} className="text-center text-xs text-gray-400 font-medium py-1">{d}</div>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-y-1" onMouseLeave={() => setHoverDate(null)}>
                {days.map((d, i) => {
                    const disabled = isDisabled(d.date);
                    const inRng = isInRange(d);
                    const isStart = isStartEdge(d);
                    const isEnd = isEndEdge(d);
                    const showHalfBg = inRng || isStart || isEnd;

                    let wrapperClass = "flex justify-center w-full py-0.5 ";
                    if (showHalfBg) {
                        wrapperClass += "bg-blue-50 ";
                    }
                    if (isStart) {
                        wrapperClass += "rounded-l-full ";
                    }
                    if (isEnd) {
                        wrapperClass += "rounded-r-full ";
                    }

                    return (
                        <div
                            key={i}
                            className={wrapperClass}
                            onMouseEnter={() => !tempEnd && setHoverDate(d.date)}
                        >
                            <button
                                onClick={() => !disabled && onSelectDay(d.date)}
                                className={getDayClass(d)}
                                disabled={disabled}
                            >
                                {d.day}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function MonthPicker({ viewDate, selectedValue, onSelect }) {
    const months = MONTH_NAMES.map((m, i) => ({ name: m.substring(0, 3), index: i }));

    const isSelected = (index) => {
        return selectedValue.getFullYear() === viewDate.getFullYear() && selectedValue.getMonth() === index;
    };

    const isDisabled = (monthIndex) => {
        const viewYear = viewDate.getFullYear();
        if (viewYear > TODAY.getFullYear()) return true;
        if (viewYear < MIN_DATE.getFullYear()) return true;
        if (viewYear === TODAY.getFullYear() && monthIndex > TODAY.getMonth()) return true;
        if (viewYear === MIN_DATE.getFullYear() && monthIndex < MIN_DATE.getMonth()) return true;
        return false;
    };

    return (
        <div className="grid grid-cols-3 gap-4 mt-4">
            {months.map((m) => {
                const disabled = isDisabled(m.index);
                return (
                    <button
                        key={m.index}
                        disabled={disabled}
                        onClick={() => {
                            const d = new Date(viewDate);
                            d.setMonth(m.index);
                            onSelect(d);
                        }}
                        className={`h-12 rounded-lg text-sm font-medium transition-all duration-200 ease-in-out border
              ${isSelected(m.index)
                                ? 'bg-blue-500 text-white border-blue-500 shadow-md'
                                : disabled
                                    ? 'border-transparent text-gray-300 cursor-not-allowed bg-gray-50'
                                    : 'border-transparent text-gray-700 hover:bg-gray-100 hover:border-gray-200'}`}
                    >
                        {m.name}
                    </button>
                )
            })}
        </div>
    );
}

function YearPicker({ viewDate, selectedValue, onSelect }) {
    const currentViewYear = viewDate.getFullYear();
    const startYear = Math.floor(currentViewYear / 12) * 12;
    const years = Array.from({ length: 12 }, (_, i) => startYear + i);

    const isSelected = (y) => selectedValue.getFullYear() === y;
    const isDisabled = (y) => y > TODAY.getFullYear() || y < MIN_DATE.getFullYear();

    return (
        <div className="grid grid-cols-3 gap-4 mt-4">
            {years.map((year) => {
                const disabled = isDisabled(year);
                return (
                    <button
                        key={year}
                        disabled={disabled}
                        onClick={() => {
                            const d = new Date(viewDate);
                            d.setFullYear(year);
                            onSelect(d);
                        }}
                        className={`h-12 rounded-lg text-sm font-medium transition-all duration-200 ease-in-out border
              ${isSelected(year)
                                ? 'bg-blue-500 text-white border-blue-500 shadow-md'
                                : disabled
                                    ? 'border-transparent text-gray-300 cursor-not-allowed bg-gray-50'
                                    : 'border-transparent text-gray-700 hover:bg-gray-100 hover:border-gray-200'}`}
                    >
                        {year}
                    </button>
                );
            })}
        </div>
    );
}

function CustomSelect({ value, options, onChange, minWidth = "min-w-[100px]" }) {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectedOption = options.find(o => o.value === value);

    return (
        <div className={`relative ${minWidth}`} ref={containerRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center justify-between w-full bg-white border text-gray-700 py-1 pl-3 pr-2 rounded-md shadow-sm text-sm font-medium transition-all duration-200
                ${isOpen ? 'border-blue-500 ring-1 ring-blue-500 text-blue-600' : 'border-gray-300 hover:border-gray-400'}`}
            >
                <span className="truncate mr-2">{selectedOption?.label}</span>
                <BsChevronDown className={`w-3 h-3 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute top-full left-0 mt-1 w-full max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 animate-slide-in origin-top">
                    {options.map((opt) => (
                        <button
                            key={opt.value}
                            disabled={opt.disabled}
                            onClick={() => {
                                if (!opt.disabled) {
                                    onChange(opt.value);
                                    setIsOpen(false);
                                }
                            }}
                            className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between transition-all duration-200
                                ${opt.value === value ? 'text-blue-600 font-semibold bg-blue-50' : 'text-gray-700'}
                                ${opt.disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-blue-50 hover:text-blue-600'}
                            `}
                        >
                            <span>{opt.label}</span>
                            {opt.value === value && <IoMdCheckmark className="w-3 h-3" />}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function CalendarContent({ type, selectedValue, viewDate, setViewDate, onSelect, tempStart, tempEnd, hoverDate, setHoverDate, onSelectDay, rightViewDate, setRightViewDate }) {
    const canNextMonth = () => {
        if (viewDate.getFullYear() === TODAY.getFullYear() && viewDate.getMonth() >= TODAY.getMonth()) return false;
        return true;
    };

    const canPrevMonth = () => {
        if (viewDate.getFullYear() === MIN_DATE.getFullYear() && viewDate.getMonth() <= MIN_DATE.getMonth()) return false;
        return true;
    };

    const canNextYear = () => {
        return viewDate.getFullYear() < TODAY.getFullYear();
    };

    const canPrevYear = () => {
        return viewDate.getFullYear() > MIN_DATE.getFullYear();
    };

    const nextMonth = () => {
        if (!canNextMonth()) return;
        const d = new Date(viewDate);
        d.setMonth(d.getMonth() + 1);
        setViewDate(d);
    };

    const prevMonth = () => {
        if (!canPrevMonth()) return;
        const d = new Date(viewDate);
        d.setMonth(d.getMonth() - 1);
        setViewDate(d);
    };

    const nextYear = () => {
        if (!canNextYear()) return;
        const d = new Date(viewDate);
        d.setFullYear(d.getFullYear() + 1);
        setViewDate(d);
    };

    const prevYear = () => {
        if (!canPrevYear()) return;
        const d = new Date(viewDate);
        d.setFullYear(d.getFullYear() - 1);
        setViewDate(d);
    };

    const changeYearPage = (offset) => {
        const d = new Date(viewDate);
        d.setFullYear(d.getFullYear() + offset);
        setViewDate(d);
    }

    const generateYears = () => {
        const currentYear = TODAY.getFullYear();
        const start = 2020;
        const years = [];
        for (let i = start; i <= currentYear; i++) {
            years.push(i);
        }
        return years;
    };
    const years = generateYears();

    const getMonthOptions = (year) => {
        return MONTH_NAMES.map((name, i) => {
            const isFutureMonth = year === TODAY.getFullYear() && i > TODAY.getMonth();
            const isPastMonth = year === MIN_DATE.getFullYear() && i < MIN_DATE.getMonth();
            return {
                label: name,
                value: i,
                disabled: isFutureMonth || isPastMonth
            };
        });
    };

    const yearOptions = years.map((year) => ({
        label: year.toString(),
        value: year,
        disabled: false
    }));

    const renderHeader = () => {
        if (type === 'year') {
            const startYear = Math.floor(viewDate.getFullYear() / 12) * 12;
            const endYear = startYear + 11;
            return (
                <div className="flex items-center justify-between mb-4">
                    <Tooltip content="Previous Years" delay={0} closeDelay={0}>
                        <button onClick={() => changeYearPage(-12)} className="p-1 hover:bg-gray-100 rounded text-gray-600 disabled:opacity-30 transition-colors duration-200"><BsChevronLeft className="w-3 h-3" /></button>
                    </Tooltip>
                    <span className="font-bold text-gray-700">{startYear} - {endYear}</span>
                    <Tooltip content="Next Years" delay={0} closeDelay={0}>
                        <button onClick={() => changeYearPage(12)} className="p-1 hover:bg-gray-100 rounded text-gray-600 disabled:opacity-30 transition-colors duration-200"><BsChevronRight className="w-3 h-3" /></button>
                    </Tooltip>
                </div>
            );
        }

        const monthOptions = MONTH_NAMES.map((name, i) => {
            const isFutureMonth = viewDate.getFullYear() === TODAY.getFullYear() && i > TODAY.getMonth();
            const isPastMonth = viewDate.getFullYear() === MIN_DATE.getFullYear() && i < MIN_DATE.getMonth();
            return {
                label: name,
                value: i,
                disabled: isFutureMonth || isPastMonth
            };
        });

        const yearOptions = years.map((year) => ({
            label: year.toString(),
            value: year,
            disabled: false
        }));

        return (
            <div className="flex items-center justify-between mb-4 gap-2">
                <Tooltip content={type === 'month' ? "Previous Year" : "Previous Month"} delay={0} closeDelay={0}>
                    <button
                        onClick={type === 'month' ? prevYear : prevMonth}
                        disabled={type === 'month' ? !canPrevYear() : !canPrevMonth()}
                        className="p-1.5 hover:bg-gray-100 rounded-md text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent transition-colors duration-200"
                    >
                        <BsChevronLeft className="w-4 h-4" />
                    </button>
                </Tooltip>

                <div className="flex items-center gap-2 flex-1 justify-center z-50">
                    {(type === 'date' || type === 'week') && (
                        <CustomSelect
                            value={viewDate.getMonth()}
                            options={monthOptions}
                            onChange={(val) => {
                                const d = new Date(viewDate);
                                d.setMonth(val);
                                setViewDate(d);
                            }}
                            minWidth="min-w-[110px]"
                        />
                    )}

                    <CustomSelect
                        value={viewDate.getFullYear()}
                        options={yearOptions}
                        onChange={(val) => {
                            const d = new Date(viewDate);
                            d.setFullYear(val);
                            setViewDate(d);
                        }}
                        minWidth="min-w-[90px]"
                    />
                </div>

                <Tooltip content={type === 'month' ? "Next Year" : "Next Month"} delay={0} closeDelay={0}>
                    <button
                        onClick={type === 'month' ? nextYear : nextMonth}
                        disabled={type === 'month' ? !canNextYear() : !canNextMonth()}
                        className="p-1.5 hover:bg-gray-100 rounded-md text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent transition-colors duration-200"
                    >
                        <BsChevronRight className="w-4 h-4" />
                    </button>
                </Tooltip>
            </div>
        );
    };

    if (type === 'custom') {
        const leftMonth = viewDate.getMonth();
        const leftYear = viewDate.getFullYear();
        
        const rightMonth = rightViewDate.getMonth();
        const rightYear = rightViewDate.getFullYear();

        const prevLeftMonth = () => {
            const d = new Date(viewDate);
            d.setMonth(d.getMonth() - 1);
            if (d >= MIN_DATE) setViewDate(d);
        };

        const nextLeftMonth = () => {
            const d = new Date(viewDate);
            d.setMonth(d.getMonth() + 1);
            if (d <= TODAY) setViewDate(d);
        };

        const prevRightMonth = () => {
            const d = new Date(rightViewDate);
            d.setMonth(d.getMonth() - 1);
            if (d >= MIN_DATE) setRightViewDate(d);
        };

        const nextRightMonth = () => {
            const d = new Date(rightViewDate);
            d.setMonth(d.getMonth() + 1);
            if (d <= TODAY) setRightViewDate(d);
        };

        const leftMonthOptions = getMonthOptions(leftYear);
        const rightMonthOptions = getMonthOptions(rightYear);

        return (
            <div className="w-full">
                <div className="flex gap-6 mb-4 px-1 select-none z-50 relative">
                    {/* Left Header */}
                    <div className="flex-1 flex items-center justify-between gap-2 z-50">
                        <button
                            onClick={prevLeftMonth}
                            disabled={viewDate.getFullYear() === MIN_DATE.getFullYear() && viewDate.getMonth() === MIN_DATE.getMonth()}
                            className="p-1.5 hover:bg-gray-100 rounded-md text-gray-600 disabled:opacity-30 transition-all duration-200"
                        >
                            <BsChevronLeft className="w-4 h-4" />
                        </button>
                        
                        <div className="flex items-center gap-2 flex-1 justify-center">
                            <CustomSelect
                                value={leftMonth}
                                options={leftMonthOptions}
                                onChange={(val) => {
                                    const d = new Date(viewDate);
                                    d.setMonth(val);
                                    setViewDate(d);
                                }}
                                minWidth="min-w-[100px]"
                            />
                            <CustomSelect
                                value={leftYear}
                                options={yearOptions}
                                onChange={(val) => {
                                    const d = new Date(viewDate);
                                    d.setFullYear(val);
                                    setViewDate(d);
                                }}
                                minWidth="min-w-[80px]"
                            />
                        </div>
                        <button
                            onClick={nextLeftMonth}
                            disabled={viewDate.getFullYear() === TODAY.getFullYear() && viewDate.getMonth() === TODAY.getMonth()}
                            className="p-1.5 hover:bg-gray-100 rounded-md text-gray-600 disabled:opacity-30 transition-all duration-200"
                        >
                            <BsChevronRight className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="h-full border-r border-transparent"></div>

                    {/* Right Header */}
                    <div className="flex-1 flex items-center justify-between gap-2 z-50">
                        <button
                            onClick={prevRightMonth}
                            disabled={rightViewDate.getFullYear() === MIN_DATE.getFullYear() && rightViewDate.getMonth() === MIN_DATE.getMonth()}
                            className="p-1.5 hover:bg-gray-100 rounded-md text-gray-600 disabled:opacity-30 transition-all duration-200"
                        >
                            <BsChevronLeft className="w-4 h-4" />
                        </button>
                        
                        <div className="flex items-center gap-2 flex-1 justify-center">
                            <CustomSelect
                                value={rightMonth}
                                options={rightMonthOptions}
                                onChange={(val) => {
                                    const d = new Date(rightViewDate);
                                    d.setMonth(val);
                                    setRightViewDate(d);
                                }}
                                minWidth="min-w-[100px]"
                            />
                            <CustomSelect
                                value={rightYear}
                                options={yearOptions}
                                onChange={(val) => {
                                    const d = new Date(rightViewDate);
                                    d.setFullYear(val);
                                    setRightViewDate(d);
                                }}
                                minWidth="min-w-[80px]"
                            />
                        </div>

                        <button
                            onClick={nextRightMonth}
                            disabled={rightViewDate.getFullYear() === TODAY.getFullYear() && rightViewDate.getMonth() === TODAY.getMonth()}
                            className="p-1.5 hover:bg-gray-100 rounded-md text-gray-600 disabled:opacity-30 transition-all duration-200"
                        >
                            <BsChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                <div className="flex gap-6">
                    <div className="flex-1">
                        <RangeDayPicker
                            viewDate={viewDate}
                            tempStart={tempStart}
                            tempEnd={tempEnd}
                            hoverDate={hoverDate}
                            setHoverDate={setHoverDate}
                            onSelectDay={onSelectDay}
                        />
                    </div>
                    <div className="h-full border-r border-gray-100"></div>
                    <div className="flex-1">
                        <RangeDayPicker
                            viewDate={rightViewDate}
                            tempStart={tempStart}
                            tempEnd={tempEnd}
                            hoverDate={hoverDate}
                            setHoverDate={setHoverDate}
                            onSelectDay={onSelectDay}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            {renderHeader()}
            <div className="min-h-[240px]">
                {(type === 'date' || type === 'week') && (
                    <DayPicker
                        viewDate={viewDate}
                        selectedValue={selectedValue}
                        onSelect={onSelect}
                        isWeekMode={type === 'week'}
                    />
                )}
                {type === 'month' && (
                    <MonthPicker
                        viewDate={viewDate}
                        selectedValue={selectedValue}
                        onSelect={onSelect}
                    />
                )}
                {type === 'year' && (
                    <YearPicker
                        viewDate={viewDate}
                        selectedValue={selectedValue}
                        onSelect={onSelect}
                    />
                )}
            </div>
        </div>
    );
}

export default function SwitchableDatePicker({
    selectedLineDate,
    setSelectedLineDate,
    selectedValue,
    onSelect,
    pickerType,
    setPickerType
}) {
    // Resolve props
    const activeSetDate = setSelectedLineDate || onSelect;

    // Internal resolver: If selectedLineDate is an object {startDate...}, convert to Date for display
    const activeDate = useMemo(() => {
        const raw = selectedLineDate || selectedValue;
        if (!raw) return TODAY;
        // If it's the requested Object format, parse startDate
        if (raw.startDate) return new Date(raw.startDate);
        // If it's a valid Date object
        if (raw instanceof Date) return raw;
        // Fallback
        return TODAY;
    }, [selectedLineDate, selectedValue]);

    const [isOpen, setIsOpen] = useState(false);
    const [showTypeDropdown, setShowTypeDropdown] = useState(false);
    const [viewDate, setViewDate] = useState(activeDate);
    const [rightViewDate, setRightViewDate] = useState(new Date(activeDate.getFullYear(), activeDate.getMonth() + 1, 1));
    const containerRef = useRef(null);

    // Range picker states for custom range selection
    const [tempStart, setTempStart] = useState(null);
    const [tempEnd, setTempEnd] = useState(null);
    const [hoverDate, setHoverDate] = useState(null);

    useEffect(() => {
        if (isOpen && pickerType === 'custom') {
            if (selectedLineDate && selectedLineDate.startDate && selectedLineDate.endDate) {
                setTempStart(new Date(selectedLineDate.startDate));
                setTempEnd(new Date(selectedLineDate.endDate));
            } else {
                setTempStart(null);
                setTempEnd(null);
            }
        }
    }, [isOpen, pickerType, selectedLineDate]);

    // Sync viewDate and rightViewDate when activeDate changes significantly (and we are not open navigating)
    useEffect(() => {
        if (!isOpen && activeDate) {
            setViewDate(activeDate);
            setRightViewDate(new Date(activeDate.getFullYear(), activeDate.getMonth() + 1, 1));
        }
    }, [activeDate, isOpen]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
                setShowTypeDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const getDisplayValue = () => {
        if (pickerType === 'custom') {
            if (selectedLineDate && selectedLineDate.startDate && selectedLineDate.endDate) {
                const start = new Date(selectedLineDate.startDate);
                const end = new Date(selectedLineDate.endDate);
                const formatDate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                return `${formatDate(start)} to ${formatDate(end)}`;
            }
            return "Select Custom Days";
        }

        if (!activeDate) return "";
        const y = activeDate.getFullYear();
        const m = activeDate.getMonth();
        const d = activeDate.getDate();

        switch (pickerType) {
            case 'date':
                return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            case 'week':
                const weekNum = getWeekNumber(activeDate);
                return `${y}-${weekNum}${getOrdinal(weekNum)} Week`;
            case 'month':
                return `${y}-${String(m + 1).padStart(2, '0')}`;
            case 'year':
                return `${y}`;
            default:
                return "";
        }
    };

    const getOrdinal = (n) => {
        const s = ["th", "st", "nd", "rd"];
        const v = n % 100;
        return s[(v - 20) % 10] || s[v] || s[0];
    };

    const handleTypeChange = (type) => {
        setPickerType(type);
        setShowTypeDropdown(false);

        const refDate = activeDate || TODAY;
        setViewDate(refDate);
        setIsOpen(true);

        if (activeSetDate) {
            // Calculate IST start/end of day in UTC by subtracting 5 hours and 30 minutes (19,800,000 ms)
            const getStartOfDayUTC = (y, m, d) => new Date(Date.UTC(y, m, d, 0, 0, 0, 0) - 19800000).toISOString();
            const getEndOfDayUTC = (y, m, d) => new Date(Date.UTC(y, m, d, 23, 59, 59, 999) - 19800000).toISOString();

            let startStr, endStr;
            const y = refDate.getFullYear();
            const m = refDate.getMonth();
            const d = refDate.getDate();

            if (type === 'date') {
                startStr = getStartOfDayUTC(y, m, d);
                endStr = getEndOfDayUTC(y, m, d);
            } else if (type === 'week') {
                const { start, end } = getWeekRange(refDate);
                startStr = getStartOfDayUTC(start.getFullYear(), start.getMonth(), start.getDate());
                endStr = getEndOfDayUTC(end.getFullYear(), end.getMonth(), end.getDate());
            } else if (type === 'month') {
                const lastDay = new Date(y, m + 1, 0).getDate();
                startStr = getStartOfDayUTC(y, m, 1);
                endStr = getEndOfDayUTC(y, m, lastDay);
            } else if (type === 'year') {
                startStr = getStartOfDayUTC(y, 0, 1);
                endStr = getEndOfDayUTC(y, 11, 31);
            } else if (type === 'custom') {
                // If switching to custom, keep current values or set to last 7 days
                if (selectedLineDate && selectedLineDate.startDate && selectedLineDate.endDate) {
                    startStr = selectedLineDate.startDate;
                    endStr = selectedLineDate.endDate;
                } else {
                    const sevenDaysAgo = new Date(TODAY);
                    sevenDaysAgo.setDate(TODAY.getDate() - 7);
                    startStr = getStartOfDayUTC(sevenDaysAgo.getFullYear(), sevenDaysAgo.getMonth(), sevenDaysAgo.getDate());
                    endStr = getEndOfDayUTC(TODAY.getFullYear(), TODAY.getMonth(), TODAY.getDate());
                }
            }

            activeSetDate({ startDate: startStr, endDate: endStr });
        }
    };

    // !!! FIXED TIME CALCULATION LOGIC !!!
    const handleDateSelect = (date) => {
        if (activeSetDate) {
            // Calculate IST start/end of day in UTC by subtracting 5 hours and 30 minutes (19,800,000 ms)
            const getStartOfDayUTC = (y, m, d) => new Date(Date.UTC(y, m, d, 0, 0, 0, 0) - 19800000).toISOString();
            const getEndOfDayUTC = (y, m, d) => new Date(Date.UTC(y, m, d, 23, 59, 59, 999) - 19800000).toISOString();

            let startStr, endStr;
            const y = date.getFullYear();
            const m = date.getMonth();
            const d = date.getDate();

            if (pickerType === 'date') {
                startStr = getStartOfDayUTC(y, m, d);
                endStr = getEndOfDayUTC(y, m, d);
            } else if (pickerType === 'week') {
                const { start, end } = getWeekRange(date);
                startStr = getStartOfDayUTC(start.getFullYear(), start.getMonth(), start.getDate());
                endStr = getEndOfDayUTC(end.getFullYear(), end.getMonth(), end.getDate());
            } else if (pickerType === 'month') {
                const lastDay = new Date(y, m + 1, 0).getDate();
                startStr = getStartOfDayUTC(y, m, 1);
                endStr = getEndOfDayUTC(y, m, lastDay);
            } else if (pickerType === 'year') {
                startStr = getStartOfDayUTC(y, 0, 1);
                endStr = getEndOfDayUTC(y, 11, 31);
            }

            activeSetDate({ startDate: startStr, endDate: endStr });
        }
        setIsOpen(false);
    };

    const handleSelectDay = (date) => {
        if (!tempStart || (tempStart && tempEnd)) {
            setTempStart(date);
            setTempEnd(null);
        } else {
            if (date < tempStart) {
                setTempStart(date);
            } else {
                setTempEnd(date);
                
                const getStartOfDayUTC = (y, m, d) => new Date(Date.UTC(y, m, d, 0, 0, 0, 0) - 19800000).toISOString();
                const getEndOfDayUTC = (y, m, d) => new Date(Date.UTC(y, m, d, 23, 59, 59, 999) - 19800000).toISOString();
                
                const startStr = getStartOfDayUTC(tempStart.getFullYear(), tempStart.getMonth(), tempStart.getDate());
                const endStr = getEndOfDayUTC(date.getFullYear(), date.getMonth(), date.getDate());
                
                if (activeSetDate) {
                    activeSetDate({ startDate: startStr, endDate: endStr });
                }
                setIsOpen(false);
            }
        }
    };

    return (
        <div className="flex flex-col items-center justify-center">
            <style>{animationStyles}</style>
            <div className="relative flex items-center gap-2" ref={containerRef}>
                <div className="relative">
                    <Select
                        className="min-w-[120px] w-28 md:w-auto"
                        variant="bordered"
                        selectedKeys={[pickerType]}
                        onChange={(e) => {
                            if (e.target.value) {
                                handleTypeChange(e.target.value);
                            }
                        }}
                        aria-label="Picker Type"
                    >
                        <SelectItem key="date" className="capitalize">Date</SelectItem>
                        <SelectItem key="week" className="capitalize">Week</SelectItem>
                        <SelectItem key="month" className="capitalize">Month</SelectItem>
                        <SelectItem key="year" className="capitalize">Year</SelectItem>
                        <SelectItem key="custom" className="capitalize">Custom Days</SelectItem>
                    </Select>
                </div>

                <div className="relative">
                    <div
                        onClick={() => {
                            setIsOpen(!isOpen);
                        }}
                        className={`flex items-center justify-between min-w-[200px] px-3 py-2 bg-white border rounded-md shadow-sm cursor-pointer transition-colors duration-200
              ${isOpen ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-300 hover:border-blue-400'}`}
                    >
                        <span className="text-sm text-gray-700 font-medium">
                            {getDisplayValue()}
                        </span>
                        <CiCalendar className="text-gray-400 w-4 h-4 ml-3" />
                    </div>

                    {isOpen && (
                        <div className={`absolute top-full right-0 mt-2 p-4 bg-white border border-gray-200 rounded-xl shadow-xl z-40 animate-slide-in origin-top-right transition-all duration-250 ${pickerType === 'custom' ? 'w-[640px]' : 'w-[320px]'}`}>
                            <CalendarContent
                                type={pickerType}
                                selectedValue={activeDate}
                                viewDate={viewDate}
                                setViewDate={setViewDate}
                                onSelect={handleDateSelect}
                                tempStart={tempStart}
                                tempEnd={tempEnd}
                                hoverDate={hoverDate}
                                setHoverDate={setHoverDate}
                                onSelectDay={handleSelectDay}
                                rightViewDate={rightViewDate}
                                setRightViewDate={setRightViewDate}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}